import { $config } from '@client/core/atoms/config.js';
import { $wallpaperConfig } from '@client/core/atoms/wallpaperConfigAtom.js';
import { Brand, debugLog } from '@schibsted-nmp/advertising-shared';

import { CreativeMessageEvent } from './CreativeMessageEvent.js';
import { fetchAllElements, fetchElement, styleElements } from './domUtils.js';

export namespace WallpaperHandler {
  export type Data = CreativeMessageEvent.Data.Value<
    CreativeMessageEvent.Type.SetWallpaper,
    { config: $wallpaperConfig.Value }
  >;

  const mainContentsQueries = [
    '#mobility-item-page-root',
    '#app',
    '.page-container',
    '#frontpage-content'
  ];

  let observer: MutationObserver | null;
  let clickEvent: (event: MouseEvent) => void;
  let wallpaperContainerRef: HTMLElement | null;

  export function initiate() {
    window.addEventListener('message', (event: CreativeMessageEvent) => {
      // called from wallpaper creative to initate wallpaper
      // this will listen on all global post messages
      // but we want to filter it down to -> allowed origins and setWallpaper type
      if (!CreativeMessageEvent.isGoogleSafeFrameOrigin(event.origin)) return;
      if (event.data.type !== CreativeMessageEvent.Type.SetWallpaper) return;

      $wallpaperConfig.set(event.data.config);
    });

    $wallpaperConfig.subscribe((config) => {
      if (!config.stickySkyscrapers) {
        handleSkyscraperStickySetting(false);
      }

      clickEvent = (event) => {
        if (event.defaultPrevented) return;
        window.open(config.clickThroughUrl, '_blank');
      };

      if (config.wallpaperImage || config.wallpaperHtml) {
        const { body } = document;
        body.style.pointerEvents = 'none';
        const wallpaperContainer = document.createElement('div');

        wallpaperContainer.id = 'advt-wallpaper-container';
        wallpaperContainer.style.zIndex = '-1';
        wallpaperContainer.style.pointerEvents = 'all';
        wallpaperContainer.style.top = `51px`;
        wallpaperContainer.style.backgroundPosition = 'center top';

        wallpaperContainer.style.height = config.stickyWallpaper
          ? '100vh'
          : '1080px';

        wallpaperContainer.style.position = config.stickyWallpaper
          ? 'fixed'
          : 'absolute';

        const wallpaperDiv = document.createElement(
          config.wallpaperHtml ? 'iframe' : 'div'
        );

        if (config.wallpaperImage) {
          wallpaperDiv.style.backgroundImage = `url(${config.wallpaperImage})`;
        } else if (config.wallpaperHtml) {
          (wallpaperDiv as HTMLIFrameElement).src = config.wallpaperHtml;
        }

        if (config.setNoRepeatBackground) {
          wallpaperDiv.style.backgroundRepeat = 'no-repeat';
        }

        wallpaperDiv.style.width = '100%';
        wallpaperDiv.style.height = '100%';
        wallpaperDiv.style.pointerEvents = 'none';
        wallpaperDiv.style.backgroundPosition = 'center top';

        document.body.insertBefore(
          wallpaperContainer,
          document.body.firstChild
        );
        wallpaperContainer.appendChild(wallpaperDiv);
        wallpaperContainerRef = wallpaperContainer;

        const header = fetchElement('finn-topbar');
        if (header) header.style.marginBottom = '0';

        const mainContents = fetchAllElements(
          `body > *:not(script)${mainContentsQueries.map((query) => `:not(${query})`).join('')}`
        );

        const mainContents2 = fetchAllElements(
          mainContentsQueries.map((query) => `body > ${query}`).join(',')
        );

        requestAnimationFrame(() => {
          for (const content of mainContents)
            content.style.pointerEvents = 'all';

          for (const content of mainContents2) {
            for (const child of Array.from(content.children)) {
              if (!(child instanceof HTMLElement)) continue;

              if (child.id !== 'banner-top-container') {
                child.style.pointerEvents = 'all';
              } else {
                child.style.pointerEvents = 'none';

                const actualTopBanner = child.children[0] as
                  | HTMLHtmlElement
                  | undefined;

                if (actualTopBanner)
                  actualTopBanner.style.pointerEvents = 'all';
              }
            }
          }
        });

        if (config.clickThroughUrl) {
          wallpaperContainer.style.cursor = 'pointer';
          wallpaperContainer.addEventListener('click', clickEvent);
        } else {
          wallpaperContainer.style.cursor = 'default';
        }
      }

      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            if (node.tagName !== 'SCRIPT') node.style.pointerEvents = 'all';
          }
        }
      });

      observer.observe(document.body, { childList: true });

      const parentElement = fetchElement('#advt_wallpaper');

      if (parentElement) {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.removedNodes) {
              if (
                node instanceof HTMLIFrameElement ||
                node instanceof HTMLScriptElement
              ) {
                debugLog('Iframe or script removed! Running cleanup...');
                cleanup();
              }
            }

            // Check if the iframe/script is no longer in the DOM
            const iframeExists = parentElement.querySelector('iframe');
            const scriptExists = parentElement.querySelector('script');

            if (!iframeExists && !scriptExists) {
              debugLog('No iframe or script found! Running cleanup...');
              cleanup();
            }
          }
        });

        observer.observe(parentElement, { childList: true, subtree: true });
      }

      debugLog('wallpaper intiate');
    });
  }

  function handleSkyscraperStickySetting(sticky: boolean) {
    const stickyElements = fetchAllElements(
      '.banners__sticky--1 .banners__skyscraper'
    );

    styleElements(stickyElements, {
      position: sticky ? 'sticky' : 'static'
    });
  }

  // This should observe adnami midscroll placement and determine two things:
  // 1. If the current scroll position is above the midscroll elements top, the wallpaper should be
  //    fixed and appear "sticky" — shown across the entire page.
  // 2. If the current scroll position is below the midscroll elements top, the wallpaper is set to
  //    absolute and positioned closer to the current scroll position. This makes the wallpaper
  //    disappear under the midscroll placement.

  let midscrollObserver: IntersectionObserver | null = null;
  let hasActiveScrollListener = false;

  export function setupAdnamiMidscrollObserver() {
    const { brand } = $config.get() ?? {};
    if (brand !== Brand.Tori) return;

    const midscroll = fetchElement('#advt_middle_2');
    if (!midscroll) return;

    const offset = 351;
    const midscrollTop = midscroll.getBoundingClientRect().top - offset;

    let isFixed = true;

    const handleScrollPosition = () => {
      if (!wallpaperContainerRef) return;

      const { scrollY } = window;
      if (isFixed && scrollY > midscrollTop) {
        wallpaperContainerRef.style.position = 'absolute';
        wallpaperContainerRef.style.top = `${window.scrollY}px`;
        isFixed = false;
      } else if (!isFixed && scrollY < midscrollTop) {
        wallpaperContainerRef.style.position = 'fixed';
        wallpaperContainerRef.style.top = '51px';
        isFixed = true;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          if (!hasActiveScrollListener) {
            window.addEventListener('scroll', handleScrollPosition);
            hasActiveScrollListener = true;
          }
        } else if (hasActiveScrollListener) {
          window.removeEventListener('scroll', handleScrollPosition);
          hasActiveScrollListener = false;
        }
      },
      {
        threshold: [0, 1]
      }
    );

    observer.observe(midscroll);
    midscrollObserver = observer;
  }

  export function cleanup() {
    debugLog('wallpaper cleanup');

    if (wallpaperContainerRef) {
      wallpaperContainerRef.remove();
    }

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (midscrollObserver) {
      midscrollObserver.disconnect();
      midscrollObserver = null;
    }

    window.removeEventListener('beforeunload', cleanup);
    handleSkyscraperStickySetting(true);
  }
}
